'use client';
import { getMangoTracker, postPageView } from '@pulsex/services';
import { getWidgetGrouping } from '@/utils/getWidgetGrouping';
import { useState, useEffect, useMemo } from 'react';
import WidgetRender from '@/components/WidgetRender';
import { useWidgetPage } from '@pulsex/hooks';
import { Widget } from '@pulsex/types';
import { Logo } from '@/icons/Logo';
import { locale } from '@/locale';
import useDailyPdf from '@/hooks/useDailyPdf';
import SubMenuNav from './SubMenuNav/Index';
import React from 'react';
import { LoadMoreTrigger } from './LazyLoader';
import pulseConfig, { IS_CANONICAL_DEPLOYMENT, IS_LOCAL } from '@/pulsex.config';
import SpinnerIcon from '@/icons/SpinnerIcon';

interface PageBuilderProps {
    url: string;
    isBeta: string | boolean;
    isMobile: boolean;
    isApple: boolean;
    pageData?: any;
    assetsData?: any;
    dailyPdf?: any;
    initialGroupedStates?: any;
    nonce?: any;
}

const BATCH_SIZE = 5;

export const PageBuilder = ({
    url,
    isBeta,
    isMobile,
    isApple,
    pageData,
    assetsData,
    dailyPdf,
    initialGroupedStates,
    nonce,
}: PageBuilderProps) => {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
    const menuItems = useMemo(() => assetsData?.menus?.['h-main'] || [], [assetsData]);

    // const pathname = usePathname();

    const [documentId, setDocumentId] = useState<any>();
    const [documentGuid, setDocumentGuid] = useState<any>(undefined);
    const [documentType, setDocumentType] = useState<any>(undefined);

    // , pageData, {theme: "akhbar2025",}
    const { data, isLoading } = useWidgetPage(pulseConfig, url);

    const { data: dailyPdfData, isLoading: isDailyPdfLoading } = useDailyPdf(pulseConfig, dailyPdf);

    const currentGroupedStates = useMemo(() => {
        if (data && data.widgets) {
            return getWidgetGrouping(data);
        }

        if (initialGroupedStates) {
            return initialGroupedStates;
        }

        if (pageData && pageData.widgets) {
            return getWidgetGrouping(pageData);
        }
        return {};
    }, [data, initialGroupedStates, pageData]);

    const totalWidgets = data?.widgets?.length ?? pageData?.widgets?.length ?? 0;
    const shouldLazyLoad = useMemo(() => totalWidgets > BATCH_SIZE, [totalWidgets]);
    const [isZiadRahbaniPage, setIsZiadRahbaniPage] = useState(false);

    useEffect(() => {
        if (!IS_LOCAL && IS_CANONICAL_DEPLOYMENT && (data || pageData)) {
            const relevantData = data || pageData;
            setDocumentGuid(relevantData.postId || relevantData.id);
            setDocumentId(relevantData.id);
            if (data?.seo?.type) setDocumentType(data?.seo?.type);
        }
    }, [data, pageData]);

    useEffect(() => {
        if (!IS_LOCAL && IS_CANONICAL_DEPLOYMENT && documentGuid && documentType && url && documentId) {
            const getCookie = (name: string) => {
                var cookieString = document.cookie;
                var cookies = cookieString.split('; ');
                for (var i = 0; i < cookies.length; i++) {
                    var cookie = cookies[i].split('=');
                    if (cookie[0] === name) {
                        return decodeURIComponent(cookie[1]);
                    }
                }
                return null;
            };

            // Function to generate a unique user ID
            const generateUserId = () => {
                return 'user_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
            };

            // Function to set a cookie with a given name, value, and expiration date
            const setCookie = (name: string, value: string, days: number) => {
                var expires = new Date();
                expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
                document.cookie =
                    name +
                    '=' +
                    encodeURIComponent(value) +
                    ';expires=' +
                    expires.toUTCString() +
                    ';path=/;' +
                    `SameSite=Lax;` +
                    `Secure`;
            };

            let generatedUserId = getCookie('generatedUserId');
            if (!generatedUserId) {
                generatedUserId = generateUserId();
                setCookie('generatedUserId', generatedUserId, 10);
            }

            postPageView(
                pulseConfig,
                generatedUserId,
                documentId,
                documentType,
                'es.al-akhbar.com'
            ).catch((error) => {
                console.error('Failed to post page view:', error);
            });
        }

        if (!IS_LOCAL && documentId && url) {
            getMangoTracker(pulseConfig, documentId).catch((error) => {
                console.error('Failed to send mango tracker view:', error);
            });
        }
    }, [documentGuid, documentType, documentId, url]);

    const displayData = data || pageData;

    const styleToBackgroundUrl: Record<string, string> = {
        'ziad-rahbani-bio': '/images/ziad-background.png',
    };

    const backgroundImageUrl = useMemo(() => {
        const widgets = displayData?.widgets ?? [];
        const listToScan = shouldLazyLoad ? widgets.slice(0, visibleCount) : widgets;
        for (const widget of listToScan) {
            const url = styleToBackgroundUrl[(widget as any)?.style as string];
            if (url) return url;
        }
        return '';
    }, [displayData, shouldLazyLoad, visibleCount]);

    if (
        !isLoading &&
        (!displayData || !displayData?.widgets?.length || displayData?.widgets?.length === 0)
    ) {
        return (
            <>
                <div className="flex items-center h-screen flex-col space-y-4 max-md:space-y-6 py-10">
                    <Logo className="fill-black" size={75} />

                    <div className="text-4xl font-bold max-md:text-2xl">صفحة غير موجودة</div>

                    <p className="text-lg text-[#4c4c4c] flex max-md:px-4 max-md:text-center">
                        عذراً، فإن الصفحة التي تبحث عنها غير موجودة أو تم حذفها.. يرجى التأكد من
                        الرابط أو التوجه الى
                    </p>
                    <a href="/" className="text-masterRed text-xl border-none rtl:mr-1 ltr:ml-1">
                        {locale.HomePage}
                    </a>
                </div>
            </>
        );
    }

    if (isLoading && !pageData) {
        return (
            <main className="min-h-screen w-full container grid place-items-center">
                <SpinnerIcon className="w-12 h-12 fill-masterRed" />
            </main>
        );
    }
    const widgetsToRender = displayData?.widgets;

    return (
        <div
            style={{
                backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : '',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                paddingBottom: backgroundImageUrl ? 100 : 0,
            }}
        >
            <main className="min-h-screen block w-full container">
                <SubMenuNav data={menuItems} />
                {shouldLazyLoad
                    ? widgetsToRender?.slice(0, visibleCount).map((item: Widget, index: number) => {
                          return (
                              <React.Fragment key={`${item?.style}-${index}`}>
                                  <WidgetRender
                                      item={item}
                                      data={data}
                                      menuItems={menuItems}
                                      groupedStates={currentGroupedStates}
                                      isMobile={isMobile}
                                      dailyPdfData={dailyPdfData}
                                      isApple={isApple}
                                      nonce={nonce}
                                  />

                                  {index + 1 === visibleCount && visibleCount < totalWidgets && (
                                      <LoadMoreTrigger
                                          onVisible={() =>
                                              setVisibleCount((prev) =>
                                                  Math.min(prev + BATCH_SIZE, totalWidgets)
                                              )
                                          }
                                      />
                                  )}
                              </React.Fragment>
                          );
                      })
                    : widgetsToRender?.map((item: Widget, index: number) => {
                          return (
                              <WidgetRender
                                  key={`${item?.style}-${index}`}
                                  item={item}
                                  data={data}
                                  menuItems={menuItems}
                                  groupedStates={currentGroupedStates}
                                  isMobile={isMobile}
                                  dailyPdfData={dailyPdfData}
                                  isApple={isApple}
                                  nonce={nonce}
                              />
                          );
                      })}
            </main>
        </div>
    );
};
